import { useCallback } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { ProcuradorUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/procurador-use-case'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { showToast } from '@/infra/sonner'
import type { TCliente } from '@/types/cliente'

type TUseDadosProcuradorProps = {
  refetch: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  onNext: () => void
  compradores: TCompradoresCadastrados
  emailsJaVinculados: Record<string, string>
  nacionalidadePrincipalId: string
}

export function useDadosProcuradorPJ({
  onNext,
  compradores,
  refetch,
  emailsJaVinculados,
  nacionalidadePrincipalId,
}: TUseDadosProcuradorProps) {
  const { trigger, setValue, watch } = useFormContext<CompradorPessoaJuridicaSchemaInput>()
  const compradorAtual = watch('responsavel')
  const cpfProcurador = compradorAtual?.dados?.cpf ?? ''
  const buscouCpf = compradorAtual?.dados?.buscouCpf ?? false
  const cpfRaw = cpfProcurador.replaceAll(/\D/g, '')

  const useCase = new ProcuradorUseCase(compradores, emailsJaVinculados, nacionalidadePrincipalId)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok && error === 'MISSING_CPF') {
      trigger('responsavel.dados.cpf')
      return
    }

    if (!ok && error === 'NEED_SEARCH') {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
      })
      return
    }

    if (await trigger('responsavel.dados')) onNext()
  }, [cpfProcurador, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        setValue('responsavel', fetchResult.patch)
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('responsavel.dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw])

  const handleLimpar = useCallback(() => {
    setValue('responsavel', useCase.limparProcurador(), { shouldValidate: false })
  }, [setValue])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
